import type { CatalogueFit } from '@aioe/shared';

import { ChoiceSelect, inputClass } from './ui.tsx';

/** The form's words for an agent's or a role's fit (the set-up flow chooses agents by it). */
export interface FitDraft {
  modelClass: 'none' | 'fast' | 'standard' | 'strong';
  suits: string;
  tools: string;
}

export const blankFit: FitDraft = { modelClass: 'none', suits: '', tools: '' };

const CLASS_TEXT: Record<FitDraft['modelClass'], string> = { none: 'Not set', fast: 'Fast', standard: 'Standard', strong: 'Strong' };
const words = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);

export const fitDraftOf = (fit?: CatalogueFit): FitDraft => ({ modelClass: fit?.modelClass ?? 'none', suits: (fit?.suits ?? []).join(', '), tools: (fit?.tools ?? []).join(', ') });

/** The wire shape, or undefined when nothing is set. */
export function fitOfDraft(d: FitDraft): CatalogueFit | undefined {
  const suits = words(d.suits);
  const tools = words(d.tools);
  const fit: CatalogueFit = { ...(d.modelClass !== 'none' ? { modelClass: d.modelClass } : {}), ...(suits.length ? { suits } : {}), ...(tools.length ? { tools } : {}) };
  return Object.keys(fit).length ? fit : undefined;
}

/** "Strong model · suits api, data · 3 tools": one quiet line, empty when nothing is set. */
export function fitSentence(fit?: CatalogueFit): string {
  if (!fit) return '';
  return [fit.modelClass ? `${CLASS_TEXT[fit.modelClass]} model` : '', fit.suits?.length ? `suits ${fit.suits.join(', ')}` : '', fit.tools?.length ? `${fit.tools.length} ${fit.tools.length === 1 ? 'tool' : 'tools'}` : ''].filter(Boolean).join(' · ');
}

/** Three controls in their own row. */
export function FitFields({ value, onChange }: { value: FitDraft; onChange: (v: FitDraft) => void }) {
  return (
    <div className="grid gap-3 sm:grid-cols-[1fr_1.5fr_1.5fr]">
      <label className="grid gap-1 text-xs text-muted">
        Model class
        <ChoiceSelect label="Model class" className="w-full" value={value.modelClass} options={CLASS_TEXT} onChange={(modelClass) => onChange({ ...value, modelClass })} />
      </label>
      <label className="grid gap-1 text-xs text-muted">
        Suits (project types, comma separated)
        <input className={inputClass} value={value.suits} onChange={(e) => onChange({ ...value, suits: e.target.value })} placeholder="api, web, data" />
      </label>
      <label className="grid gap-1 text-xs text-muted">
        Tools it needs (comma separated)
        <input className={`${inputClass} mono`} value={value.tools} onChange={(e) => onChange({ ...value, tools: e.target.value })} placeholder="search, run_tests" />
      </label>
    </div>
  );
}
